/**
 * Guardrails on a move (3.2, 4.2): a visit may move to another day, and to
 * another clinician, and what the clinician sees while it moves is what they
 * would otherwise have to remember — the ordered frequency this week, whether
 * the 30-day period is at risk, what the patient said at the welcome call.
 *
 * Guardrails warn and never block. Only three things block: the past, a
 * clinician whose discipline cannot deliver the visit, and a missing trait.
 * Window, pace and patient constraints are warnings with a reason; proceeding
 * just moves the visit, and the consequence surfaces through the patient's own
 * derived rows (frequency shortfall, visits at risk), never as a row that names
 * who moved what.
 */
import type { DateKey, DemoState, DisciplineGroup, PlanVisit } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey, dateKeyRange, daysBetweenKeys, formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { effectivePlacement, evaluateConstraints } from "./constraints";
import { complianceWindowFor, selectOnPace, type OnPace } from "./lupa";

import { disciplineGroupFor, hasTraits, rosterEntries } from "./roster";
import { territoryForZip } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import { atWall, plainSentence, type TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

export type GuardLevel = "ok" | "warn" | "block";

/**
 * **What a guard said, as a code** (MY-WEEK-INSPECT-PROMPT.md, stage 2). The
 * sentences stay as they are for every surface that prints them; My week's day
 * marks and its Move anyway dialog choose their closed-list text by these codes
 * rather than by matching sentences.
 *
 * `outsideWeek` is leaving the visit's own start-of-care week, which is one event
 * with the frequency shortfall it causes; `periodThreshold` is the 30-day period
 * dropping below its threshold, and only that — `checks.periodRisk` restates the
 * shortfall, so it is not a code of its own.
 */
export type GuardCode = "past" | "discipline" | "traits" | "outsideWeek" | "recertWindow" | "patientDays" | "patientHours" | "patientOther" | "periodThreshold" | "weekBehind";

export interface MoveDay {
  date: DateKey;
  level: GuardLevel;
  /** `level !== "block"` — kept for callers that only ask "can I drop here". */
  allowed: boolean;
  reason?: TimeSentence;
}

/**
 * What a move would do to the three things the branch cares about — **each one
 * absent when there is nothing to say about it** (Pete, 2026-09-22: *"many of
 * the fields are often empty … we should drop them when they're empty"*).
 *
 * Only the cast carries an episode plan, so a drag on any of the eighty-odd
 * generated census visits has no orders behind it and cannot answer how many of
 * this week's visits would survive or what the 30-day period would risk. Those
 * two used to render as an em dash each, which reads on a screen as a number
 * that failed to load rather than a question nobody can ask. They are now
 * simply not there, and the strip says what it knows.
 */
export interface GuardChecks {
  /**
   * "1 of 2" — the routine visits of the moved visit's own discipline group its
   * start-of-care week keeps, against those ordered. A start of care, an
   * evaluation or a rule visit is an order of its own, not part of "2 a week", so
   * moving one reports no frequency. Absent with no plan behind the visit.
   */
  frequency?: string;
  /** The start-of-care week `frequency` counts, 1-based, and the discipline group it counts. */
  frequencyWeek?: number;
  frequencyGroup?: DisciplineGroup;
  /** "none" or "1 visit" — ordered visits the 30-day period would have at risk. Absent with no plan. */
  periodRisk?: string;
  /** "matched" or the patient-window violation, phrased by the view. Absent when the patient has no constraints on file. */
  window?: TimeSentence;
}

export interface MoveEvaluation {
  visit: PlanVisit;
  window: { start: DateKey; end: DateKey };
  days: MoveDay[];
  level: GuardLevel;
  /** `level !== "block"`. */
  allowed: boolean;
  /** Every reason, blocks first. Phrased by the view. */
  reasons: TimeSentence[];
  blocks: TimeSentence[];
  warnings: TimeSentence[];
  /** One code per reason, in the same order. */
  codes: GuardCode[];
  checks: GuardChecks;
  paceBefore: OnPace;
  paceAfter: OnPace;
}

/** Visits that count as ordered in a SOC-anchored week: everything not canceled. */
function countInWeek(visits: PlanVisit[], week: { start: DateKey; end: DateKey }): number {
  return visits.filter((v) => v.targetDate >= week.start && v.targetDate <= week.end && v.status !== "canceled").length;
}

/**
 * A visit that counts toward an ordered weekly frequency: routine work, not a
 * start of care, an evaluation or a rule visit (supervisory, recert, ROC). It
 * used to count every discipline's every visit, so Whitfield's first week — SOC,
 * a PT evaluation and two SN visits — read "3 of 4" when one SN visit left it
 * (Pete, 2026-10-08).
 */
export function isRoutine(state: Pick<DemoState, "config">, visit: PlanVisit): boolean {
  return !visit.rule && !state.config.visitTypes[visit.visitTypeKind].isAssessment && !visit.visitTypeKind.endsWith("_EVAL");
}

/**
 * The arrival window a move would put the visit in. My week moves a visit to
 * another day at the hour it already holds, so it passes none; My day moves it
 * to another hour on the same day, where the hour is the whole point — without
 * it the patient-window check would keep reporting the slot the visit came from.
 */
export interface MoveWindow {
  /** "HH:mm" branch-zone wall clock. */
  start: string;
  end: string;
}

export function evaluateMove(
  state: DemoState,
  planVisitId: string,
  toDate: DateKey,
  toHandle?: string,
  wallNow: Date = new Date(),
  toWindow?: MoveWindow,
): MoveEvaluation | undefined {
  const visit = state.planVisits[planVisitId];
  const episode = visit ? state.episodes[visit.episodeId] : undefined;
  const patient = episode ? state.patients[episode.patientRef] : undefined;
  if (!visit || !episode || !patient) return undefined;
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, wallNow), tz);
  /**
   * **The week the visit sits in** (Pete, 2026-10-08). A visit pinned to a
   * working day, or moved by a clinician, keeps the week number its plan gave
   * it; judged against that week, Osei's Monday on a Friday seed read next week's
   * days as inside its week and its own Tuesday as "Week 5 drops to 2 of 2". So
   * the week is the start-of-care week of the day the visit actually sits on, and
   * every visit is counted on its own actual day. A visit inside its plan week —
   * every visit nobody has moved — gets the same week as before. The recert keeps
   * its own window, days 56 to 60.
   */
  const sitsOn = effectivePlacement(state, visit).targetDate;
  const weekIndex = Math.floor(daysBetweenKeys(episode.socDate, sitsOn) / 7);
  const window =
    visit.rule === "recert"
      ? complianceWindowFor(visit, episode)
      : { start: addDaysToKey(episode.socDate, weekIndex * 7), end: addDaysToKey(episode.socDate, weekIndex * 7 + 6) };
  const mine = Object.values(state.planVisits).filter((v) => v.episodeId === episode.id);
  const paceBefore = selectOnPace(mine, episode, state.config, today);
  const moved: PlanVisit = {
    ...visit,
    targetDate: toDate,
    providerHandle: toHandle ?? visit.providerHandle,
    windowStart: toWindow?.start ?? visit.windowStart,
    windowEnd: toWindow?.end ?? visit.windowEnd,
  };
  const paceAfter = selectOnPace(mine.map((v) => (v.id === visit.id ? moved : v)), episode, state.config, today);
  const clinician = moved.providerHandle ? rosterEntries(state).find((e) => e.handle === moved.providerHandle) : undefined;

  const days: MoveDay[] = dateKeyRange(window.start, window.end).map((date) => {
    if (date < today) return { date, level: "block", allowed: false, reason: "in the past" };
    const hard = evaluateConstraints(patient, { targetDate: date });
    if (hard.length) return { date, level: "warn", allowed: true, reason: hard[0].reason };
    return { date, level: "ok", allowed: true };
  });

  const blocks: TimeSentence[] = [];
  const warnings: TimeSentence[] = [];
  const blockCodes: GuardCode[] = [];
  const warningCodes: GuardCode[] = [];
  // Two reasons are the same reason when they read the same; a sentence is parts
  // rather than a string now, so the de-duplication compares what they say.
  const said = (reason: TimeSentence) => plainSentence(reason, tz);
  const alreadyWarned = (reason: TimeSentence) => warnings.some((w) => said(w) === said(reason));
  const block = (reason: TimeSentence, code: GuardCode) => {
    blocks.push(reason);
    blockCodes.push(code);
  };
  const warn = (reason: TimeSentence, code: GuardCode) => {
    if (alreadyWarned(reason)) return;
    warnings.push(reason);
    warningCodes.push(code);
  };
  const constraintCode = (kind: string): GuardCode => (kind === "availabilityDays" ? "patientDays" : kind === "availabilityWindow" ? "patientHours" : "patientOther");
  if (toDate < today) block(`${toDate} is in the past`, "past");
  if (clinician) {
    if (!state.config.visitTypes[visit.visitTypeKind].disciplines.includes(clinician.discipline)) block(`${clinician.discipline} does not deliver ${state.config.visitTypes[visit.visitTypeKind].label}`, "discipline");
    if (!hasTraits(clinician, visit.requiredTraits)) block(`${clinician.displayName} is missing ${visit.requiredTraits.filter((t) => !clinician.traits.includes(t)).join(", ")}`, "traits");
  }
  if (toDate < window.start || toDate > window.end)
    warn(`${toDate} is outside the compliance window ${window.start} – ${window.end}${visit.rule === "recert" ? " (days 56–60)" : " (SOC-anchored week)"}`, visit.rule === "recert" ? "recertWindow" : "outsideWeek");
  const patientDay = evaluateConstraints(patient, { targetDate: toDate });
  for (const h of patientDay) warn(h.reason, constraintCode(h.constraint.kind));
  if (clinician) {
    const hard = evaluateConstraints(patient, moved, clinician).filter((h) => h.constraint.kind !== "availabilityDays");
    for (const h of hard) warn(h.reason, constraintCode(h.constraint.kind));
  }
  if (paceAfter.periodStatus === "belowThreshold" && paceBefore.periodStatus !== "belowThreshold") warn(`Period ${paceAfter.period + 1} would drop below its threshold (${paceAfter.lupaThreshold})`, "periodThreshold");
  if (paceAfter.weekStatus === "behind" && paceBefore.weekStatus !== "behind") warn(`Week ${paceAfter.weekIndex + 1} would fall behind its ordered frequency`, "weekBehind");

  const windowConstraint = patient.constraints.find((c) => c.kind === "availabilityWindow");
  const windowText: TimeSentence | undefined = !patient.constraints.length
    ? undefined
    : patientDay.length
      ? patientDay[0].reason
      : windowConstraint && moved.windowStart && moved.windowEnd && evaluateConstraints(patient, moved).some((h) => h.constraint.kind === "availabilityWindow")
        ? ["outside ", atWall(windowConstraint.value.split("-")[0] ?? ""), "–", atWall(windowConstraint.value.split("-")[1] ?? "")]
        : "matched";
  // The two numbers the clinician would otherwise have to remember, both about the
  // week the visit belongs to rather than the week she happens to be looking at:
  // how many of that week's ordered visits the move keeps, and how many ordered
  // visits the move puts at risk of not being delivered — the LUPA shortfall for
  // the period, or a visit pushed out of its own week, whichever is worse.
  const group = disciplineGroupFor(visit.discipline);
  const counted = isRoutine(state, visit);
  const sameOrder = (v: PlanVisit) => disciplineGroupFor(v.discipline) === group && isRoutine(state, v);
  const placed = mine.filter(sameOrder).map((v) => (v.id === visit.id ? { ...v, targetDate: sitsOn } : { ...v, targetDate: effectivePlacement(state, v).targetDate }));
  const orderedThisWeek = counted ? countInWeek(placed, window) : 0;
  const keptThisWeek = counted ? countInWeek(placed.map((v) => (v.id === visit.id ? { ...v, targetDate: toDate } : v)), window) : 0;
  const shortfall = Math.max(0, orderedThisWeek - keptThisWeek);
  const atRisk = Math.max(paceAfter.visitsAtRisk, shortfall);
  const checks: GuardChecks = {
    ...(counted ? { frequency: `${keptThisWeek} of ${orderedThisWeek}`, frequencyWeek: weekIndex + 1, frequencyGroup: group } : {}),
    periodRisk: atRisk > 0 ? `${atRisk} visit${atRisk === 1 ? "" : "s"}` : "none",
    window: windowText,
  };
  const level: GuardLevel = blocks.length ? "block" : warnings.length ? "warn" : "ok";
  return { visit, window, days, level, allowed: level !== "block", reasons: [...blocks, ...warnings], blocks, warnings, codes: [...blockCodes, ...warningCodes], checks, paceBefore, paceAfter };
}

/**
 * What a **Move within window** row action offers first (4.2): the same clinician's
 * next working day inside the compliance window, at the time the visit already
 * holds. The unfilled 13:00 visit therefore opens on Thursday 13:00 and the person
 * working the call-out confirms rather than composes.
 *
 * Only a visit whose own day has arrived gets a suggestion. A visit still ahead of
 * today has a day somebody chose, and the dialog opens on it.
 */
export function defaultMoveTarget(state: DemoState, planVisitId: string, wallNow: Date = new Date()): { date: DateKey; time: string } | undefined {
  const ev = evaluateMove(state, planVisitId, state.planVisits[planVisitId]?.targetDate ?? "", undefined, wallNow);
  if (!ev) return undefined;
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, wallNow), tz);
  if (ev.visit.targetDate > today) return undefined;
  const handle = ev.visit.providerHandle;
  const providerId = handle ? state.meta.bindings.providerIdsByHandle[handle] : undefined;
  const real = ev.visit.realVisitId !== undefined ? state.server.visitResources.find((v) => v.visitId === ev.visit.realVisitId) : undefined;
  const time = real ? formatInBranchZone(new Date(real.startIso), tz, "HH:mm") : ev.visit.windowStart ?? "10:00";
  const working = (date: DateKey) =>
    providerId === undefined ||
    state.server.shifts.some((s) => s.providerId === providerId && s.status === "confirmed" && !s.overflow && toDateKey(new Date(s.startIso), tz) === date);
  const date = ev.days.find((d) => d.date > today && d.level !== "block" && working(d.date))?.date;
  return date ? { date, time } : undefined;
}

export interface RealMoveEvaluation {
  level: GuardLevel;
  allowed: boolean;
  reasons: TimeSentence[];
  /** One code per reason, in the same order. */
  codes: GuardCode[];
  checks: GuardChecks;
  /** Set when the real visit is linked to a plan visit and the full evaluation ran. */
  plan?: MoveEvaluation;
}

/**
 * A real visit on My week: through its plan visit when one is linked, otherwise
 * the patient's constraints alone ("no constraints on file" when there are none).
 */
export function evaluateRealVisitMove(
  state: DemoState,
  realVisitId: number,
  toDate: DateKey,
  toHandle?: string,
  wallNow: Date = new Date(),
  toWindow?: MoveWindow,
): RealMoveEvaluation {
  const planVisit = Object.values(state.planVisits).find((v) => v.realVisitId === realVisitId);
  if (planVisit) {
    const plan = evaluateMove(state, planVisit.id, toDate, toHandle, wallNow, toWindow);
    if (plan) return { level: plan.level, allowed: plan.allowed, reasons: plan.reasons, codes: plan.codes, checks: plan.checks, plan };
  }
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, wallNow), tz);
  const vr = state.server.visitResources.find((v) => v.visitId === realVisitId);
  const patientRef = vr ? Object.entries(state.meta.bindings.patientIdsByRef).find(([, id]) => id === vr.patientId)?.[0] : undefined;
  const patient = patientRef ? state.patients[patientRef] : undefined;
  const blocks: TimeSentence[] = [];
  const warnings: TimeSentence[] = [];
  const codes: GuardCode[] = [];
  if (toDate < today) {
    blocks.push(`${toDate} is in the past`);
    codes.push("past");
  }
  const clinician = toHandle ? state.roster[toHandle] : undefined;
  if (clinician && vr) {
    const territory = territoryForZip(state.config.branch, vr.patientZip);
    void territory;
  }
  // Nothing is known about a patient with no constraints on file, so the strip
  // says nothing about them rather than saying that it knows nothing.
  let window: TimeSentence | undefined;
  if (patient && patient.constraints.length) {
    const hard = evaluateConstraints(patient, { targetDate: toDate, windowStart: toWindow?.start, windowEnd: toWindow?.end }, clinician);
    for (const h of hard) {
      warnings.push(h.reason);
      codes.push(h.constraint.kind === "availabilityDays" ? "patientDays" : h.constraint.kind === "availabilityWindow" ? "patientHours" : "patientOther");
    }
    window = hard.length ? hard[0].reason : "matched";
  }
  const level: GuardLevel = blocks.length ? "block" : warnings.length ? "warn" : "ok";
  // No plan behind this visit, so frequency and period risk are unanswerable.
  return { level, allowed: level !== "block", reasons: [...blocks, ...warnings], codes, checks: { window } };
}
